import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Clock, Calendar } from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { api } from "../../lib/api";

/**
 * EmployeeHistory — read-only view of the employee's completed cleanings.
 * Groups entries by month and shows counts so the employee can track their
 * workload over time.
 */
export default function EmployeeHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bookings", { params: { status: "completed" } })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  // Sort by date desc + group by month
  const byMonth = {};
  for (const b of [...items].sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date))) {
    const key = b.scheduled_date.slice(0, 7); // YYYY-MM
    if (!byMonth[key]) byMonth[key] = [];
    byMonth[key].push(b);
  }
  const months = Object.keys(byMonth).sort().reverse();
  const totalMinutes = items.reduce((s, b) => s + (b.duration || 0), 0);
  const totalHours = Math.floor(totalMinutes / 60);

  return (
    <div className="space-y-10">
      <div>
        <div className="eyebrow mb-3">O meu percurso</div>
        <h1 className="font-display text-5xl">Histórico de limpezas.</h1>
        <div className="text-sm text-[#a0b2af] mt-2">
          Todas as limpezas que concluiu, por ordem cronológica.
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total concluídas" value={items.length} />
        <StatCard label="Horas trabalhadas" value={`${totalHours}h`} accent="gold" />
        <StatCard label="Meses ativos" value={months.length} />
      </div>

      {loading && <div className="text-[#a0b2af] text-sm">A carregar…</div>}

      {!loading && items.length === 0 && (
        <div className="lux-card p-10 text-center text-[#a0b2af] text-sm" data-testid="emp-history-empty">
          Ainda não concluiu nenhuma limpeza. Quando finalizar uma tarefa, ela aparecerá aqui.
        </div>
      )}

      {months.map(m => (
        <section key={m} data-testid={`history-month-${m}`}>
          <div className="eyebrow mb-4 flex items-center gap-2">
            <Calendar size={13} className="text-[#d4af37]" />
            {format(parseISO(m + "-01"), "MMMM yyyy", { locale: pt })}
            <span className="text-[#a0b2af] normal-case tracking-normal text-[11px]">· {byMonth[m].length} limpeza{byMonth[m].length > 1 ? "s" : ""}</span>
          </div>
          <div className="space-y-2">
            {byMonth[m].map(b => (
              <Link key={b.id} to={`/funcionario/tarefa/${b.id}`}
                    className="lux-card p-4 flex items-center justify-between hover:border-[#d4af37]/60 transition-colors"
                    data-testid={`history-item-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs text-[#d4af37]">{b.apt_type}</span>
                    <span className="text-sm">{b.scheduled_date} · {b.scheduled_time}</span>
                    <span className="text-[10px] text-[#10b981] border border-[#10b981]/40 px-1.5 py-0.5 uppercase tracking-widest">Concluído</span>
                  </div>
                  {b.apartment_name && <div className="text-xs text-[#a0b2af] mt-1">{b.apartment_name}</div>}
                  {b.apartment_address && (
                    <div className="text-[10px] text-[#a0b2af] mt-0.5 truncate flex items-center gap-1">
                      <MapPin size={10} className="text-[#d4af37]" /> {b.apartment_address}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  <div className="text-right">
                    <div className="text-sm text-[#d4af37]">{b.total.toFixed(2)}€</div>
                    <div className="text-[10px] text-[#a0b2af] flex items-center gap-1 justify-end">
                      <Clock size={10} /> {Math.floor(b.duration / 60)}h{b.duration % 60 ? ` ${b.duration % 60}m` : ""}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-[#a0b2af]" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function StatCard({ label, value, accent = "neutral" }) {
  const colorMap = { gold: "text-[#d4af37]", neutral: "text-[#fdfbf7]" };
  return (
    <div className="lux-card p-5">
      <div className="eyebrow text-[10px]">{label}</div>
      <div className={`font-display text-3xl mt-2 ${colorMap[accent]}`}>{value}</div>
    </div>
  );
}
